import "./LocationFiltersComponent.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import CommonService from "../../../shared/services/common.service";
import {Field, FieldProps, Form, Formik, FormikProps, FormikValues} from "formik";
import FormikAutoCompleteDropdown
    from "../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FormikDatePickerComponent from "../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import {APIConfig} from "../../../constants";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import formValidationRules from "../../../shared/services/validation.service";
import * as Yup from "yup";

interface LocationFiltersComponentProps {
    filtersData: any;
    onFiltersApply: (filterData: any) => void;
    clearAllFilters: () => void;
}

const validationSchema = Yup.object().shape({
    startDate: Yup.mixed().nullable().when("endDate", {
        is: (endDate: Date) => {
            return Boolean(endDate)
        },
        then: Yup.mixed().nullable().required("Start date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    }),
    endDate: Yup.mixed().nullable().when("startDate", {
        is: (startDate: Date) => {
            return Boolean(startDate)
        },
        then: Yup.mixed().nullable().required("End date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    })
}, [['startDate', 'endDate']]);

const LocationFiltersComponent = (props: LocationFiltersComponentProps) => {

    const {onFiltersApply, filtersData, clearAllFilters} = props;
    const [dataFiltersInitialState, setDataFiltersInitialState] = useState(filtersData);
    const {
        facilityTypesList,
        facilityListLite,
        userList,
        locationStatusList,
    } = useSelector((state: IRootReducerState) => state.staticData);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const onFiltersApplyHandler = useCallback((values: any) => {
        const payload = {
            ...values
        }
        payload.types = payload.locationTypesObjs.map((item: any) => item.code);
        payload.facilityIds = payload?.facilityIdsObjs.map((item: any) => item.id);
        payload.statuses = payload?.statusesObjs?.map((item: any) => item?.code);
        payload.cities = payload.citiesObjs.map((item: any) => item.city);
        payload.createdByIds = payload.createdByObjs.map((item: any) => item.id);
        payload.startDate = payload.startDate ? CommonService.convertDateFormat(values['startDate'], 'YYYY-MM-DD') : "";
        payload.endDate = payload.endDate ? CommonService.convertDateFormat(values['endDate'], 'YYYY-MM-DD') : "";
        onFiltersApply(payload);
    }, [onFiltersApply]);

    useEffect(() => {
        setDataFiltersInitialState(filtersData);
    }, [filtersData]);

    return (
        <div className={'location-filters-component screen t-form-screen'}>
            <>
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Filters"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef.current && formikRef.current.resetForm();
                             clearAllFilters();
                         }}
                    >
                        Clear all
                    </div>
                </div>
            </>
            <div className="t-form t-form-drawer">
                <Formik
                    initialValues={dataFiltersInitialState}
                    enableReinitialize={true}
                    onSubmit={onFiltersApplyHandler}
                    innerRef={formikRef}
                    validationSchema={validationSchema}
                >
                    {({isSubmitting, values, isValid, setFieldValue, validateForm, errors, dirty}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [values, errors, validateForm]);
                        return (
                            <Form noValidate={true} className={"location-filters-holder"}>
                                <div className="t-form-controls-wrapper">
                                    <Field name={'locationTypesObjs'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    fullWidth={true}
                                                    label={'Location Type'}
                                                    multiple={true}
                                                    data={facilityTypesList}
                                                    valueKey={"code"}
                                                    placeholder={'Select Location Type'}
                                                    formikField={field}
                                                    getOptionLabel={(option => (option.title) || '')}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={'facilityIdsObjs'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    fullWidth={true}
                                                    label={'Facility Name'}
                                                    multiple={true}
                                                    data={facilityListLite}
                                                    valueKey={"id"}
                                                    placeholder={'Select Facility Name'}
                                                    formikField={field}
                                                    getOptionLabel={(option => (option.name) || '')}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={'createdByObjs'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    fullWidth={true}
                                                    label={'Added by'}
                                                    multiple={true}
                                                    data={userList}
                                                    valueKey={"id"}
                                                    placeholder={'Select Added by'}
                                                    formikField={field}
                                                    getOptionLabel={(option => (option.firstName + ' ' + option.lastName) || '')}
                                                />
                                            )
                                        }
                                    </Field>

                                    <Field name={'citiesObjs'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    label={'City'}
                                                    placeholder={"start type for city"}
                                                    formikField={field}
                                                    url={APIConfig.CITY_LIST.URL}
                                                    method={APIConfig.CITY_LIST.METHOD}
                                                    searchMode={'serverSide'}
                                                    openOnFocus={true}
                                                    dataListKey={"data.docs"}
                                                    data={[]}
                                                    multiple={true}
                                                    valueKey={'city'}
                                                    getOptionLabel={(option => (option.city) || '')}
                                                />
                                            )
                                        }
                                    </Field>

                                    <Field name={'statusesObjs'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    fullWidth={true}
                                                    label={'Location Status'}
                                                    multiple={true}
                                                    data={locationStatusList}
                                                    valueKey={"code"}
                                                    placeholder={'Select Location Status'}
                                                    formikField={field}
                                                    getOptionLabel={(option) => (option.title)}
                                                />
                                            )
                                        }
                                    </Field>

                                    <Field name={'startDate'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikDatePickerComponent
                                                    label={'Start Date'}
                                                    formikField={field}
                                                    placeholder={'Select End Date'}
                                                    maxDate={formValidationRules.today}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={'endDate'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikDatePickerComponent
                                                    label={'End Date'}
                                                    formikField={field}
                                                    placeholder={'Select End Date'}
                                                    maxDate={formValidationRules.today}
                                                />
                                            )
                                        }
                                    </Field>
                                </div>
                                <div className="drawer-actions form-action-section">
                                    <ButtonComponent variant={"contained"}
                                                     fullWidth={true}
                                                     color={"primary"}
                                                     type={"submit"}
                                                     disabled={!isValid || !dirty}
                                    >
                                        Apply
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        </div>
    );

};

export default LocationFiltersComponent;
